// src/features/simulators/shared/components/ApiRequestLog.tsx

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Collapse,
  Button,
  Menu,
  MenuItem as MuiMenuItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  alpha,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowRight as CollapseIcon,
  FileDownload as DownloadIcon,
  Code as CodeIcon,
  Http as HttpIcon,
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import type { CapturedRequest } from '../types/simulator.types';
import { toCurl, toPostmanCollection, toHttpFile, downloadFile } from '../utils/apiExport';

interface ApiRequestLogProps {
  requests: CapturedRequest[];
  simulatorName?: string;
  autoScroll?: boolean;
}

const MethodChip: React.FC<{ method: string }> = ({ method }) => (
  <Chip
    label={method}
    size="small"
    sx={{
      fontFamily: 'monospace',
      fontWeight: 700,
      fontSize: '0.65rem',
      height: 20,
      backgroundColor: method === 'POST' ? '#e3f2fd' : method === 'PUT' ? '#fff3e0' : method === 'DELETE' ? '#fce4ec' : '#f3e5f5',
      color: method === 'POST' ? '#1565c0' : method === 'PUT' ? '#e65100' : method === 'DELETE' ? '#c62828' : '#7b1fa2',
      '& .MuiChip-label': { px: 0.75 },
    }}
  />
);

const StatusChip: React.FC<{ status: number }> = ({ status }) => {
  const isOk = status >= 200 && status < 300;
  return (
    <Chip
      label={status}
      size="small"
      color={isOk ? 'success' : 'error'}
      variant="outlined"
      sx={{ fontFamily: 'monospace', fontWeight: 600, height: 20, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.75 } }}
    />
  );
};

const RequestDetailRow: React.FC<{ req: CapturedRequest; expanded: boolean }> = ({ req, expanded }) => {
  const theme = useTheme();
  const codeBg = theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5';

  return (
    <TableRow>
      <TableCell colSpan={6} sx={{ py: 0, px: 0, borderBottom: expanded ? undefined : 'none' }}>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ px: 2.5, py: 1.5, backgroundColor: alpha(theme.palette.background.default, 0.5) }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {req.label} — {new Date(req.timestamp).toLocaleTimeString()}
            </Typography>

            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              Headers
            </Typography>
            <Box sx={{ fontFamily: 'monospace', fontSize: '0.75rem', p: 1, mb: 1.5, borderRadius: 0.5, backgroundColor: codeBg, overflow: 'auto', maxHeight: 120 }}>
              {Object.entries(req.headers).map(([k, v]) => (
                <Box key={k}><span style={{ color: theme.palette.info.main }}>{k}</span>: {v}</Box>
              ))}
            </Box>

            {req.body && (
              <>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Request Body
                </Typography>
                <Box component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', p: 1, mb: 1.5, borderRadius: 0.5, backgroundColor: codeBg, overflow: 'auto', maxHeight: 200, m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {JSON.stringify(req.body, null, 2)}
                </Box>
              </>
            )}

            {req.responseBody && (
              <>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Response ({req.responseStatus})
                </Typography>
                <Box component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', p: 1, borderRadius: 0.5, backgroundColor: codeBg, overflow: 'auto', maxHeight: 200, m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {JSON.stringify(req.responseBody, null, 2)}
                </Box>
              </>
            )}
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
};

const RequestSummaryRow: React.FC<{
  req: CapturedRequest;
  index: number;
  expanded: boolean;
  onToggle: () => void;
}> = ({ req, index, expanded, onToggle }) => {
  const [copied, setCopied] = useState(false);

  const handleCopyCurl = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(toCurl(req));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const path = (() => {
    try { return new URL(req.url).pathname; }
    catch { return req.url; }
  })();

  return (
    <TableRow
      hover
      onClick={onToggle}
      sx={{ cursor: 'pointer', '& > td': { borderBottom: expanded ? 'none' : undefined } }}
    >
      <TableCell sx={{ width: 28, py: 0.5, px: 0.5, color: 'text.secondary' }}>
        {expanded ? <ExpandIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} /> : <CollapseIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} />}
      </TableCell>
      <TableCell sx={{ width: 56, py: 0.5, px: 0.5 }}>
        <MethodChip method={req.method} />
      </TableCell>
      <TableCell sx={{ py: 0.5, px: 0.5, maxWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: '0.75rem' }} title={path}>
        {path}
      </TableCell>
      <TableCell sx={{ width: 52, py: 0.5, px: 0.5, textAlign: 'center' }}>
        <StatusChip status={req.responseStatus} />
      </TableCell>
      <TableCell sx={{ width: 56, py: 0.5, px: 0.5, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
        {req.duration}ms
      </TableCell>
      <TableCell sx={{ width: 32, py: 0.5, px: 0, textAlign: 'center' }}>
        <Tooltip title={copied ? 'Copied!' : 'Copy as cURL'}>
          <IconButton size="small" onClick={handleCopyCurl} sx={{ p: 0.25 }}>
            <CopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

export const ApiRequestLog: React.FC<ApiRequestLogProps> = ({
  requests,
  simulatorName = 'Simulator',
  autoScroll = false,
}) => {
  const translate = useTranslate();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const userScrolledRef = useRef(false);

  const handleScroll = useCallback(() => {
    if (!containerRef.current || !autoScroll) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    userScrolledRef.current = scrollHeight - scrollTop - clientHeight > 40;
  }, [autoScroll]);

  useEffect(() => {
    if (!autoScroll || userScrolledRef.current || !containerRef.current) return;
    containerRef.current.scrollTop = containerRef.current.scrollHeight;
  }, [requests.length, autoScroll]);

  if (requests.length === 0) return null;

  const handleToggle = (id: string) => {
    setExpandedId(prev => prev === id ? null : id);
  };

  const handleExportPostman = () => {
    const json = toPostmanCollection(requests, `${simulatorName} - API Requests`);
    downloadFile(json, `${simulatorName.toLowerCase().replace(/\s+/g, '-')}-api.postman_collection.json`, 'application/json');
    setAnchorEl(null);
  };

  const handleExportHttp = () => {
    const content = toHttpFile(requests);
    downloadFile(content, `${simulatorName.toLowerCase().replace(/\s+/g, '-')}-api.http`, 'text/plain');
    setAnchorEl(null);
  };

  const handleCopyAllCurl = async () => {
    const allCurls = requests.map(r => `# ${r.label}\n${toCurl(r)}`).join('\n\n');
    await navigator.clipboard.writeText(allCurls);
    setAnchorEl(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {autoScroll && (
            <Box sx={{
              width: 8, height: 8, borderRadius: '50%',
              backgroundColor: theme.palette.success.main,
              animation: 'apiLogPulse 1.5s ease-in-out infinite',
              '@keyframes apiLogPulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.3 },
              },
            }} />
          )}
          {translate('resources.simulators.wizard.api_log_count', { count: requests.length })}
        </Typography>
        <Button
          size="small"
          startIcon={<DownloadIcon />}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ textTransform: 'none' }}
        >
          {translate('resources.simulators.wizard.api_log_export')}
        </Button>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
          <MuiMenuItem onClick={handleExportPostman}>
            <ListItemIcon><CodeIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Postman Collection</ListItemText>
          </MuiMenuItem>
          <MuiMenuItem onClick={handleExportHttp}>
            <ListItemIcon><HttpIcon fontSize="small" /></ListItemIcon>
            <ListItemText>.http (VS Code REST Client)</ListItemText>
          </MuiMenuItem>
          <MuiMenuItem onClick={handleCopyAllCurl}>
            <ListItemIcon><CopyIcon fontSize="small" /></ListItemIcon>
            <ListItemText>cURL (copy all)</ListItemText>
          </MuiMenuItem>
        </Menu>
      </Box>

      {/* Table */}
      <TableContainer
        ref={containerRef}
        onScroll={handleScroll}
        sx={{ maxHeight: 420, borderRadius: 1, border: `1px solid ${theme.palette.divider}` }}
      >
        <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 28, py: 0.5, px: 0.5 }} />
              <TableCell sx={{ ...theme.custom.table.headCell, width: 56, py: 0.5, px: 0.5 }}>Method</TableCell>
              <TableCell sx={{ ...theme.custom.table.headCell, py: 0.5, px: 0.5 }}>Path</TableCell>
              <TableCell sx={{ ...theme.custom.table.headCell, width: 52, py: 0.5, px: 0.5, textAlign: 'center' }}>Status</TableCell>
              <TableCell sx={{ ...theme.custom.table.headCell, width: 56, py: 0.5, px: 0.5, textAlign: 'right' }}>Time</TableCell>
              <TableCell sx={{ width: 32, py: 0.5, px: 0 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((req, index) => (
              <React.Fragment key={req.id}>
                <RequestSummaryRow
                  req={req}
                  index={index}
                  expanded={expandedId === req.id}
                  onToggle={() => handleToggle(req.id)}
                />
                <RequestDetailRow req={req} expanded={expandedId === req.id} />
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
